// Copyright (c) 2026 CTO's Club LLC. All rights reserved.
// Author: Alex Baretta (alex@baretta.com).

import { z } from 'zod';
import {
    LosslessNumber,
    SemanticJsonNumberTokenPattern,
    isSemanticLosslessNumber,
} from './lossless-json-codec';

export const JsonNumberTokenPattern: RegExp = SemanticJsonNumberTokenPattern;

export const JsonNumberTokenSchema = z
    .string()
    .regex(JsonNumberTokenPattern, 'Invalid JSON number token')
    .brand<'JsonNumberToken'>();

export type JsonNumberToken = z.infer<typeof JsonNumberTokenSchema>;

/**
 * Reader-only structural view of a lossless numeric value materialized from
 * stored JSON. Current writer and request contracts must not use this type.
 */
export interface StoredLosslessNumber {
    readonly isLosslessNumber: true;
    readonly value: JsonNumberToken;
}

export type StoredJsonNumber = number | StoredLosslessNumber;

export type SafeIntegerDomainProvenance =
    | {
          readonly kind: 'postgresql-smallint';
      }
    | {
          readonly kind: 'postgresql-integer';
      }
    | {
          readonly kind: 'postgresql-generated-identifier';
          readonly storage: 'smallint' | 'integer' | 'bigint';
      }
    | {
          readonly kind: 'finite-integer-domain';
          readonly values: readonly number[];
      }
    | {
          readonly kind: 'fixed-width-protocol-integer';
          readonly bits: number;
          readonly signed: boolean;
      }
    | {
          readonly kind: 'bounded-owned-schema';
          readonly schemaName: string;
          readonly minimum: number;
          readonly maximum: number;
      }
    | {
          readonly kind: 'bounded-derivation';
          readonly derivation: string;
          readonly inputDomains: readonly string[];
          readonly overflowProtection: 'excluded-by-bounds' | 'runtime-asserted';
          readonly minimum: number;
          readonly maximum: number;
      };

export interface SafeIntegerDomainDefinition {
    readonly name: string;
    readonly provenance: SafeIntegerDomainProvenance;
}

const SafeIntegerDomainBrand: unique symbol = Symbol('SafeIntegerDomain');

export interface SafeIntegerDomain {
    readonly name: string;
    readonly provenance: SafeIntegerDomainProvenance;
    readonly minimum: number;
    readonly maximum: number;
    readonly allowedValues?: readonly number[];
    readonly [SafeIntegerDomainBrand]: true;
}

function requireNonEmptySafeIntegerDomainText(value: string, fieldName: string): string {
    const normalizedValue = value.trim();
    if (normalizedValue.length === 0) {
        throw new Error(`Safe integer domain ${fieldName} must be nonempty`);
    }
    return normalizedValue;
}

function requireSafeIntegerDomainBounds(
    minimum: number,
    maximum: number
): { readonly minimum: number; readonly maximum: number } {
    if (!Number.isSafeInteger(minimum) || !Number.isSafeInteger(maximum) || minimum > maximum) {
        throw new Error('Safe integer domain bounds must be ordered safe integers');
    }
    return { minimum, maximum };
}

function deriveFixedWidthProtocolIntegerBounds(
    provenance: Extract<
        SafeIntegerDomainProvenance,
        { readonly kind: 'fixed-width-protocol-integer' }
    >
): { readonly minimum: number; readonly maximum: number } {
    if (!Number.isInteger(provenance.bits) || provenance.bits < 1 || provenance.bits > 53) {
        throw new Error('Safe fixed-width protocol integer domains require 1 to 53 bits');
    }
    if (provenance.signed) {
        const magnitude = 2 ** (provenance.bits - 1);
        return requireSafeIntegerDomainBounds(-magnitude, magnitude - 1);
    }
    return requireSafeIntegerDomainBounds(0, 2 ** provenance.bits - 1);
}

function deriveSafeIntegerDomainBounds(provenance: SafeIntegerDomainProvenance): {
    readonly minimum: number;
    readonly maximum: number;
    readonly allowedValues?: readonly number[];
} {
    switch (provenance.kind) {
        case 'postgresql-smallint':
            return { minimum: -32_768, maximum: 32_767 };
        case 'postgresql-integer':
            return { minimum: -2_147_483_648, maximum: 2_147_483_647 };
        case 'postgresql-generated-identifier':
            return {
                minimum: 1,
                maximum:
                    provenance.storage === 'smallint'
                        ? 32_767
                        : provenance.storage === 'integer'
                          ? 2_147_483_647
                          : Number.MAX_SAFE_INTEGER,
            };
        case 'finite-integer-domain': {
            if (provenance.values.length === 0) {
                throw new Error('Finite safe integer domains require at least one value');
            }
            const allowedValues = new Set(provenance.values);
            if (
                allowedValues.size !== provenance.values.length ||
                provenance.values.some((value) => !Number.isSafeInteger(value))
            ) {
                throw new Error('Finite safe integer domain values must be unique safe integers');
            }
            return {
                minimum: Math.min(...allowedValues),
                maximum: Math.max(...allowedValues),
                allowedValues: Object.freeze([...allowedValues]),
            };
        }
        case 'fixed-width-protocol-integer':
            return deriveFixedWidthProtocolIntegerBounds(provenance);
        case 'bounded-owned-schema':
            requireNonEmptySafeIntegerDomainText(provenance.schemaName, 'schemaName');
            return requireSafeIntegerDomainBounds(provenance.minimum, provenance.maximum);
        case 'bounded-derivation': {
            requireNonEmptySafeIntegerDomainText(provenance.derivation, 'derivation');
            if (provenance.inputDomains.length === 0) {
                throw new Error('Derived safe integer domains require at least one input domain');
            }
            for (const inputDomain of provenance.inputDomains) {
                requireNonEmptySafeIntegerDomainText(inputDomain, 'input domain');
            }
            return requireSafeIntegerDomainBounds(provenance.minimum, provenance.maximum);
        }
    }
}

function normalizeSafeIntegerDomainProvenance(
    provenance: SafeIntegerDomainProvenance
): SafeIntegerDomainProvenance {
    switch (provenance.kind) {
        case 'finite-integer-domain':
            return Object.freeze({
                ...provenance,
                values: Object.freeze([...provenance.values]),
            });
        case 'bounded-owned-schema':
            return Object.freeze({
                ...provenance,
                schemaName: requireNonEmptySafeIntegerDomainText(
                    provenance.schemaName,
                    'schemaName'
                ),
            });
        case 'bounded-derivation':
            return Object.freeze({
                ...provenance,
                derivation: requireNonEmptySafeIntegerDomainText(
                    provenance.derivation,
                    'derivation'
                ),
                inputDomains: Object.freeze(
                    provenance.inputDomains.map((inputDomain) =>
                        requireNonEmptySafeIntegerDomainText(inputDomain, 'input domain')
                    )
                ),
            });
        case 'postgresql-smallint':
        case 'postgresql-integer':
        case 'postgresql-generated-identifier':
        case 'fixed-width-protocol-integer':
            return Object.freeze({ ...provenance });
    }
}

export function defineSafeIntegerDomain(
    definition: SafeIntegerDomainDefinition
): SafeIntegerDomain {
    const name = requireNonEmptySafeIntegerDomainText(definition.name, 'name');
    const provenance = normalizeSafeIntegerDomainProvenance(definition.provenance);
    const bounds = deriveSafeIntegerDomainBounds(provenance);
    return Object.freeze({
        name,
        provenance,
        ...bounds,
        [SafeIntegerDomainBrand]: true as const,
    });
}

export const PostgresqlGeneratedBigintIdentifierSafeIntegerDomain = defineSafeIntegerDomain({
    name: 'postgresql.generated-bigint-identifier',
    provenance: {
        kind: 'postgresql-generated-identifier',
        storage: 'bigint',
    },
});

export type StoredJsonValue =
    | string
    | boolean
    | null
    | StoredJsonNumber
    | readonly StoredJsonValue[]
    | { readonly [key: string]: StoredJsonValue };

export type StoredJsonNumberConversionFailureReason =
    | 'invalid-stored-number'
    | 'overflow'
    | 'underflow'
    | 'non-integer'
    | 'unsafe-integer'
    | 'out-of-range'
    | 'outside-domain';

export type StoredJsonNumberConversionResult =
    | {
          readonly success: true;
          readonly value: number;
          readonly token: JsonNumberToken;
      }
    | {
          readonly success: false;
          readonly reason: StoredJsonNumberConversionFailureReason;
          readonly domain: string;
          readonly token?: JsonNumberToken;
      };

export const StoredLosslessNumberMarkerSchema: z.ZodType<true> = z.literal(true);

export const StoredLosslessNumberShapeSchema: z.ZodType<StoredLosslessNumber> = z.looseObject({
    isLosslessNumber: StoredLosslessNumberMarkerSchema,
    value: JsonNumberTokenSchema,
});

function isStoredLosslessNumber(value: unknown): value is StoredLosslessNumber {
    return StoredLosslessNumberShapeSchema.safeParse(value).success;
}

export const StoredLosslessNumberSchema: z.ZodType<StoredLosslessNumber> =
    z.custom<StoredLosslessNumber>(isStoredLosslessNumber, {
        message: 'Invalid stored lossless number',
    });

export const StoredNativeNumberSchema: z.ZodType<number> = z.number().finite();

export const StoredJsonNumberSchema: z.ZodType<StoredJsonNumber> = z.union([
    StoredNativeNumberSchema,
    StoredLosslessNumberSchema,
]);

export const StoredJsonValueSchema: z.ZodType<StoredJsonValue> = z.lazy(() =>
    z.union([
        z.string(),
        z.boolean(),
        z.null(),
        StoredJsonNumberSchema,
        z.array(StoredJsonValueSchema),
        z.record(z.string(), StoredJsonValueSchema),
    ])
);

export const StoredJsonObjectSchema: z.ZodType<Record<string, StoredJsonValue>> = z.record(
    z.string(),
    StoredJsonValueSchema
);

export const StoredJsonNumberRecordSchema: z.ZodType<Record<string, unknown>> = z.custom<
    Record<string, unknown>
>(
    (value: unknown): value is Record<string, unknown> =>
        StoredJsonObjectSchema.safeParse(value).success && !isStoredLosslessNumber(value),
    { message: 'Invalid stored JSON object' }
);

export interface StoredJsonSafeIntegerFieldContract {
    readonly safeIntegerDomain: SafeIntegerDomain;
    readonly fields: readonly string[];
}

export interface StoredJsonSafeIntegerPathContract extends StoredJsonSafeIntegerFieldContract {
    readonly path: readonly string[];
}

export interface StoredJsonLosslessNumberFieldContract {
    readonly fields: readonly string[];
}

export interface StoredJsonLosslessNumberPathContract extends StoredJsonLosslessNumberFieldContract {
    readonly path: readonly string[];
}

export function extractStoredJsonNumberToken(value: StoredJsonNumber): JsonNumberToken {
    const losslessNumberResult = StoredLosslessNumberSchema.safeParse(value);
    return losslessNumberResult.success
        ? losslessNumberResult.data.value
        : JsonNumberTokenSchema.parse(String(value));
}

function isZeroJsonNumberToken(token: JsonNumberToken): boolean {
    const coefficient = token.replace(/^[+-]/u, '').split(/[eE]/u, 1)[0]?.replace('.', '');
    return coefficient !== undefined && /^0+$/u.test(coefficient);
}

function isExactIntegerJsonNumberToken(token: JsonNumberToken): boolean {
    const match =
        /^(?:-)?(?<integer>0|[1-9]\d*)(?:\.(?<fraction>\d+))?(?:[eE](?<exponent>[+-]?\d+))?$/u.exec(
            token
        );
    if (match?.groups === undefined) {
        return false;
    }

    const fraction = match.groups.fraction ?? '';
    const exponent = BigInt(match.groups.exponent ?? '0');
    const decimalPlacesAfterExponent = BigInt(fraction.length) - exponent;
    if (decimalPlacesAfterExponent <= 0n) {
        return true;
    }

    const digits = `${match.groups.integer ?? ''}${fraction}`;
    if (decimalPlacesAfterExponent >= BigInt(digits.length)) {
        return /^0+$/u.test(digits);
    }
    const trailingDigitCount = Number(decimalPlacesAfterExponent);
    return /^0+$/u.test(digits.slice(digits.length - trailingDigitCount));
}

function conversionFailure(
    safeIntegerDomain: Pick<SafeIntegerDomain, 'name'>,
    reason: StoredJsonNumberConversionFailureReason,
    token?: JsonNumberToken
): StoredJsonNumberConversionResult {
    return {
        success: false,
        reason,
        domain: safeIntegerDomain.name,
        ...(token === undefined ? {} : { token }),
    };
}

export function convertStoredJsonNumberToSafeInteger(
    value: unknown,
    safeIntegerDomain: SafeIntegerDomain
): StoredJsonNumberConversionResult {
    const storedNumberResult = StoredJsonNumberSchema.safeParse(value);
    if (!storedNumberResult.success) {
        return conversionFailure(safeIntegerDomain, 'invalid-stored-number');
    }

    const token = extractStoredJsonNumberToken(storedNumberResult.data);
    const convertedValue = Number(token);
    if (!Number.isFinite(convertedValue)) {
        return conversionFailure(safeIntegerDomain, 'overflow', token);
    }
    if (convertedValue === 0 && !isZeroJsonNumberToken(token)) {
        return conversionFailure(safeIntegerDomain, 'underflow', token);
    }
    if (!isExactIntegerJsonNumberToken(token)) {
        return conversionFailure(safeIntegerDomain, 'non-integer', token);
    }
    if (!Number.isSafeInteger(convertedValue)) {
        return conversionFailure(safeIntegerDomain, 'unsafe-integer', token);
    }
    if (convertedValue < safeIntegerDomain.minimum || convertedValue > safeIntegerDomain.maximum) {
        return conversionFailure(safeIntegerDomain, 'out-of-range', token);
    }
    if (
        safeIntegerDomain.allowedValues !== undefined &&
        !safeIntegerDomain.allowedValues.includes(convertedValue)
    ) {
        return conversionFailure(safeIntegerDomain, 'outside-domain', token);
    }

    return { success: true, value: convertedValue, token };
}

export function normalizeStoredJsonNumberToLosslessNumber(value: unknown): unknown {
    if (isSemanticLosslessNumber(value)) {
        return value;
    }
    const storedNumberResult = StoredJsonNumberSchema.safeParse(value);
    return storedNumberResult.success
        ? new LosslessNumber(extractStoredJsonNumberToken(storedNumberResult.data))
        : value;
}

export function normalizeStoredJsonSafeIntegerObjectFields(
    value: unknown,
    contract: StoredJsonSafeIntegerFieldContract
): unknown {
    const recordResult = StoredJsonNumberRecordSchema.safeParse(value);
    if (!recordResult.success) {
        return value;
    }

    const normalized: Record<string, unknown> = { ...recordResult.data };
    for (const field of contract.fields) {
        if (!Object.hasOwn(recordResult.data, field)) {
            continue;
        }
        const conversionResult = convertStoredJsonNumberToSafeInteger(
            recordResult.data[field],
            contract.safeIntegerDomain
        );
        normalized[field] = conversionResult.success
            ? conversionResult.value
            : recordResult.data[field];
    }
    return normalized;
}

export function normalizeStoredJsonLosslessNumberObjectFields(
    value: unknown,
    contract: StoredJsonLosslessNumberFieldContract
): unknown {
    const recordResult = StoredJsonNumberRecordSchema.safeParse(value);
    if (!recordResult.success) {
        return value;
    }

    const normalized: Record<string, unknown> = { ...recordResult.data };
    for (const key of Object.keys(recordResult.data)) {
        if (!contract.fields.includes(key)) {
            continue;
        }
        normalized[key] = normalizeStoredJsonNumberToLosslessNumber(recordResult.data[key]);
    }
    return normalized;
}

export function normalizeStoredJsonSafeIntegerFieldsAtPath(
    value: unknown,
    contract: StoredJsonSafeIntegerPathContract
): unknown {
    if (contract.path.length === 0) {
        return normalizeStoredJsonSafeIntegerObjectFields(value, contract);
    }

    const recordResult = StoredJsonNumberRecordSchema.safeParse(value);
    const [key, ...remainingPath] = contract.path;
    if (!recordResult.success || key === undefined || !Object.hasOwn(recordResult.data, key)) {
        return value;
    }

    return {
        ...recordResult.data,
        [key]: normalizeStoredJsonSafeIntegerFieldsAtPath(recordResult.data[key], {
            ...contract,
            path: remainingPath,
        }),
    };
}

export function normalizeStoredJsonLosslessNumberFieldsAtPath(
    value: unknown,
    contract: StoredJsonLosslessNumberPathContract
): unknown {
    if (contract.path.length === 0) {
        return normalizeStoredJsonLosslessNumberObjectFields(value, contract);
    }

    const recordResult = StoredJsonNumberRecordSchema.safeParse(value);
    const [key, ...remainingPath] = contract.path;
    if (!recordResult.success || key === undefined || !Object.hasOwn(recordResult.data, key)) {
        return value;
    }

    return {
        ...recordResult.data,
        [key]: normalizeStoredJsonLosslessNumberFieldsAtPath(recordResult.data[key], {
            ...contract,
            path: remainingPath,
        }),
    };
}
